import classNames from 'classnames'
import React from 'react'

import type { MessageBubbleVariant } from '../../types'

import { BubbleTail } from './bubbleTail'

export interface BubbleShellProps {
  variant: MessageBubbleVariant
  text: string
  /** Draw the bubble tail. Defaults to `true`. */
  tail?: boolean
  /** Optional header rendered above the text (e.g. sender name). */
  header?: React.ReactNode
  /**
   * Width is caller-controlled — pass a width class in `className`
   * (`w-fit` to hug the text for chat/preview bubbles, `w-full` to fill the
   * container for card-like list rows). With no width class the bubble is a
   * block element and fills its container.
   */
  className?: string
}

/**
 * Surface colours and metrics are pulled from the toolkit's Stream theme vars
 * (see `styles.css`) so a standalone bubble is pixel-identical to the
 * in-channel one and re-themes from the same single source of truth.
 */
const SURFACE_STYLE_BY_VARIANT: Record<
  MessageBubbleVariant,
  React.CSSProperties
> = {
  sent: {
    backgroundColor:
      'var(--str-chat__own-message-bubble-background-color, #1e2330)',
    color: 'var(--str-chat__own-message-bubble-color, #fff)',
  },
  received: {
    backgroundColor:
      'var(--str-chat__message-bubble-background-color, #f1f0ee)',
    color: 'var(--str-chat__message-bubble-color, #000000)',
  },
}

/**
 * The rounded bubble box: theme-driven surface, channel-matched typography
 * (`0.875rem / 1.3125rem`, `0.14px` tracking, `0.75rem 1rem` padding), an
 * optional header slot, and an optional tail.
 *
 * `isolation: isolate` forms a stacking context so the `z-index: -1` tail
 * paints behind the bubble's own opaque, same-colour background but in front
 * of the surrounding page — matching the in-channel `::after` tail.
 *
 * `data-dd-privacy="mask"` masks the message text (and header) from Datadog
 * session replay by default, matching the in-channel renderers
 * (`CustomMessage`, `MediaMessage`). Since this bubble is used for standalone
 * previews of real user content outside `ChannelView`, masking must live on
 * the component rather than relying on every consumer to wrap it.
 */
const BubbleShell: React.FC<BubbleShellProps> = ({
  variant,
  text,
  tail = true,
  header,
  className,
}) => (
  <div
    data-dd-privacy="mask"
    className={classNames(
      'relative px-4 py-3',
      'text-[0.875rem] leading-[1.3125rem] tracking-[0.14px]',
      className
    )}
    style={{
      ...SURFACE_STYLE_BY_VARIANT[variant],
      borderRadius: 'var(--str-chat__message-bubble-border-radius, 1.5rem)',
      isolation: 'isolate',
    }}
  >
    {header != null && (
      <div className="mb-1 font-medium" data-testid="message-bubble-header">
        {header}
      </div>
    )}
    <p className="m-0 whitespace-pre-wrap break-words hyphens-manual">{text}</p>
    {tail && <BubbleTail variant={variant} />}
  </div>
)

export default BubbleShell
